"use client"

import { useEffect } from "react"
import { WORKOS_AUTHKIT_ENTRY_PATH } from "@/lib/auth/workos/constants"
import { mapLegacyInviteHash } from "@/lib/migration/mapLegacyInviteHash"
import { mapLegacyResetHash } from "@/lib/migration/mapLegacyResetHash"

type Props = {
  redirectTo: string | null
  /**
   * AuthKit sent a code from a sign-in it started itself (the environment's
   * default redirect lands on `/login`). We cannot redeem it — no PKCE
   * verifier, and web holds no API key — so start our own flow that accepts
   * the sign-in that just happened rather than asking for it again.
   */
  resumeRecentSignIn?: boolean
}

/**
 * Client-side because a `#fragment` never reaches the server: ClientConsole
 * invite and reset links arrive as `/#/invite?token=…`, and the proxy lands
 * them on `/login`. Entering AuthKit from the server would strand an invitee
 * at authentication before they can accept. `PublicLegacyHashRedirect` in the
 * root layout owns that navigation, so this only has to stand down.
 *
 * A full navigation, not `router.push`: the entry route is a route handler
 * that must run on the server to mint its transaction cookie.
 */
export const AuthKitEntryRedirect = ({
  redirectTo,
  resumeRecentSignIn = false,
}: Props) => {
  useEffect(() => {
    const hash = window.location.hash
    if (mapLegacyInviteHash(hash) ?? mapLegacyResetHash(hash)) return

    const target = new URL(WORKOS_AUTHKIT_ENTRY_PATH, window.location.origin)
    if (redirectTo) target.searchParams.set("redirect", redirectTo)
    if (resumeRecentSignIn) target.searchParams.set("resume", "true")
    window.location.replace(target.toString())
  }, [redirectTo, resumeRecentSignIn])

  return null
}
